import React from 'react';
import {
    FontWeights,
    getTheme,
    mergeStyleSets,
    Text,
} from '@fluentui/react';
import { Car } from '../Model/Car';

interface CarStatePanelProps {
    isVisible: boolean;
    cars: Car[];
}

const CarStatePanel: React.FC<CarStatePanelProps> = ({ isVisible, cars }) => {
    const theme = getTheme();
    const [isMobile, setIsMobile] = React.useState(window.innerWidth < 640);

    React.useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    const styles = mergeStyleSets({
        card: {
            background: theme.palette.white,
            borderRadius: '12px',
            padding: '24px 32px',
            boxShadow: theme.effects.elevation8,
            marginBottom: '40px',
            selectors: {
                '@media (max-width: 640px)': {
                    padding: '16px 14px',
                },
            },
        },
        cardHeader: {
            fontSize: '20px',
            fontWeight: FontWeights.bold,
            color: theme.palette.themeDark,
            margin: '0 0 16px 0',
        },
        carCard: {
            background: theme.palette.neutralLighterAlt,
            border: `1px solid ${theme.palette.neutralQuaternaryAlt}`,
            borderRadius: '8px',
            padding: '14px 16px',
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
        },
        carName: {
            fontWeight: FontWeights.semibold,
            fontSize: '14px',
            color: theme.palette.themeDark,
        },
        licensePlate: {
            fontSize: '12px',
            color: theme.palette.neutralSecondary,
            marginTop: '2px',
        },
        kmBadge: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'flex-end',
        },
        kmLabel: {
            fontSize: '10px',
            fontWeight: 600,
            color: theme.palette.neutralSecondary,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.4px',
            lineHeight: '1.2',
        },
        kmValue: {
            fontWeight: FontWeights.bold,
            fontSize: '18px',
            color: theme.palette.themePrimary,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: '1.2',
        },
        // Desktop table
        table: {
            width: '100%',
            borderCollapse: 'collapse' as const,
        },
        th: {
            textAlign: 'left' as const,
            fontSize: '13px',
            fontWeight: FontWeights.bold,
            color: theme.palette.neutralDark,
            padding: '10px 12px',
            borderBottom: `2px solid ${theme.palette.neutralQuaternaryAlt}`,
            background: theme.palette.neutralLighterAlt,
        },
        td: {
            padding: '12px 12px',
            fontSize: '14px',
            borderBottom: `1px solid ${theme.palette.neutralLighter}`,
            verticalAlign: 'middle' as const,
        },
        tdKm: {
            fontWeight: FontWeights.bold,
            fontSize: '16px',
            color: theme.palette.themePrimary,
            fontVariantNumeric: 'tabular-nums',
            padding: '12px 12px',
            borderBottom: `1px solid ${theme.palette.neutralLighter}`,
        },
        plateChip: {
            fontSize: '12px',
            fontWeight: FontWeights.semibold,
            color: theme.palette.neutralSecondary,
            background: theme.palette.neutralLighter,
            padding: '2px 7px',
            borderRadius: '4px',
            display: 'inline-block',
        },
    });

    if (!isVisible) return null;

    if (cars.length === 0) {
        return (
            <div className={styles.card}>
                <h3 className={styles.cardHeader}>🚗 Stav aut</h3>
                <Text style={{ color: theme.palette.neutralSecondary }}>Žádná auta nejsou k dispozici.</Text>
            </div>
        );
    }

    return (
        <div className={styles.card}>
            <h3 className={styles.cardHeader}>🚗 Stav aut</h3>

            {isMobile ? (
                <>
                    {cars.map(car => (
                        <div key={car.rowKey} className={styles.carCard}>
                            <div>
                                <div className={styles.carName}>{car.carName}</div>
                                <div className={styles.licensePlate}>{car.licensePlate}</div>
                            </div>
                            <div className={styles.kmBadge}>
                                <span className={styles.kmLabel}>Tachometr</span>
                                <span className={styles.kmValue}>
                                    {car.kilometers.toLocaleString('cs-CZ')} km
                                </span>
                            </div>
                        </div>
                    ))}
                </>
            ) : (
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={styles.th}>Auto</th>
                            <th className={styles.th}>SPZ</th>
                            <th className={styles.th}>Stav tachometru</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cars.map(car => (
                            <tr key={car.rowKey}>
                                <td className={styles.td} style={{ fontWeight: FontWeights.semibold }}>{car.carName}</td>
                                <td className={styles.td}><span className={styles.plateChip}>{car.licensePlate}</span></td>
                                <td className={styles.tdKm}>{car.kilometers.toLocaleString('cs-CZ')} km</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default CarStatePanel;
